import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import apiClient from '../utils/api';

export default function NotificationsDropdown() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/notifications');
        setNotifications(res.data || []);
      } catch (e) {
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`);
      setNotifications((prev) => prev.map((n) => (n.notification_id === notificationId ? { ...n, is_read: true } : n)));
    } catch (e) {
      setNotifications((prev) => prev.map((n) => (n.notification_id === notificationId ? { ...n, is_read: true } : n)));
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        data-testid="nav-notifications"
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        className="hover:bg-white/10 relative"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="fixed sm:absolute left-1/2 sm:left-auto sm:right-0 -translate-x-1/2 sm:translate-x-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-80 max-h-[70vh] sm:max-h-[500px] bg-gray-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-violet-900/30">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-violet-300" />
              <h3 className="font-semibold text-white">Notifications</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-white/60">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-white/50">No notifications yet</div>
            ) : (
              <div className="divide-y divide-white/10">
                {notifications.slice(0, 12).map((n) => (
                  <div key={n.notification_id} className={`p-4 ${n.is_read ? 'bg-transparent' : 'bg-white/5'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${n.is_read ? 'text-white/70' : 'text-white'}`}>{n.message}</p>
                        <p className="text-xs text-white/40 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      {!n.is_read && (
                        <button
                          onClick={() => markAsRead(n.notification_id)}
                          className="text-white/60 hover:text-white"
                          title="Mark as read"
                        >
                          <Check size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/10 bg-gray-950">
            <Button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              variant="ghost"
              className="w-full hover:bg-white/10"
            >
              View all
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
